import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AppointmentBookingDto, jobTypeLabel } from '../../interfaces/appointments';
import { buildPickList } from './buildPickList';

type PickListProps = {
  open: boolean;
  onClose: () => void;
  /** Formatted date shown in the heading, e.g. 18.02.2026 */
  dateLabel: string;
  appointments: AppointmentBookingDto[];
};

// Print only the dialog: hide the app root and dialog chrome, and drop the dark theme background.
const printStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        '#root': { display: 'none !important' },
        '.no-print': { display: 'none !important' },
        '.MuiBackdrop-root': { display: 'none !important' },
        '.pick-list-dialog.MuiDialog-root': { position: 'static !important' },
        '.pick-list-dialog .MuiDialog-container': { display: 'block !important', height: 'auto !important' },
        '.pick-list-dialog .MuiPaper-root': {
          boxShadow: 'none !important',
          maxHeight: 'none !important',
          margin: '0 !important',
          maxWidth: 'none !important',
          backgroundColor: '#fff !important',
          backgroundImage: 'none !important',
          color: '#000 !important',
        },
        '.pick-list-dialog .MuiDialogContent-root': { overflow: 'visible !important' },
        '.pick-list-dialog .MuiDialogTitle-root': { breakAfter: 'avoid' },
        '.pick-list-dialog thead': { display: 'table-header-group' },
        '.pick-list-dialog .MuiTypography-root, .pick-list-dialog .MuiTableCell-root': {
          color: '#000 !important',
          borderColor: '#000 !important',
        },
        'body': { backgroundColor: '#fff !important' },
      },
    }}
  />
);

function PickListTable({ rows }: { rows: AppointmentBookingDto[] }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Table size="small" aria-label="Plukkliste">
        <TableHead>
          <TableRow>
            <TableCell>Tidspunkt</TableCell>
            <TableCell>Plassering</TableCell>
            <TableCell>Regnr</TableCell>
            <TableCell>Kunde</TableCell>
            <TableCell>Jobbtype</TableCell>
            <TableCell>Merknad</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((appointment) => (
            <TableRow key={appointment.id} sx={{ breakInside: 'avoid' }}>
              <TableCell>{appointment.time}</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{appointment.vehicleLocation || '-'}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{appointment.registrationNumber || '-'}</TableCell>
              <TableCell>{appointment.customerName}</TableCell>
              <TableCell>{jobTypeLabel(appointment.jobType) || '-'}</TableCell>
              <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{appointment.customerNote || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PickList({ open, onClose, dateLabel, appointments }: PickListProps) {
  const rows = buildPickList(appointments);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" className="pick-list-dialog">
      {printStyles}
      <DialogTitle>Plukkliste {dateLabel} ({rows.length})</DialogTitle>
      <DialogContent>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">Ingen bookinger å plukke for denne dagen.</Typography>
        )}
        {rows.length > 0 && <PickListTable rows={rows} />}
      </DialogContent>
      <DialogActions className="no-print">
        <Button onClick={onClose}>Lukk</Button>
        <Button variant="contained" onClick={() => window.print()} disabled={rows.length === 0}>
          Skriv ut
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PickList;
